import { useEffect, useRef } from 'react';

type UseDocumentTitleOptions = {
  /**
   * If true, restores the previous title when component unmounts.
   * If false, keeps the last set title.
   * @default true
   */
  restoreOnUnmount?: boolean;
};

/**
 * Hook to dynamically update the document title.
 * Automatically restores the previous title on unmount by default.
 *
 * @param title - The title to set. Can be undefined/null (won't update in that case)
 * @param options - Configuration options
 *
 * @example
 * ```tsx
 * // Simple usage
 * useDocumentTitle('Dashboard | TheGuarantors');
 *
 * // Dynamic title with user data
 * const { data: user } = useUserQuery();
 * const userName = user ? `${user.first_name} ${user.last_name}` : 'User Details';
 * useDocumentTitle(`${userName} | TheGuarantors`);
 *
 * // Don't restore on unmount
 * useDocumentTitle('Settings | TheGuarantors', { restoreOnUnmount: false });
 * ```
 */
export const useDocumentTitle = (
  title: string | undefined | null,
  options: UseDocumentTitleOptions = {},
) => {
  const { restoreOnUnmount = true } = options;
  const prevTitleRef = useRef<string>(document.title);

  useEffect(() => {
    // Store the current title on first render
    prevTitleRef.current = document.title;
  }, []);

  useEffect(() => {
    if (title) {
      document.title = title;
    }
  }, [title]);

  useEffect(() => {
    return () => {
      if (restoreOnUnmount && prevTitleRef.current) {
        document.title = prevTitleRef.current;
      }
    };
  }, [restoreOnUnmount]);
};
